import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowUpRight, BarChart3, BriefcaseBusiness, MessageSquare, TriangleAlert, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { reportsApi } from '../../../api/reports.api';

const formatter = new Intl.NumberFormat('en-IN');

const formatNumber = (value) => formatter.format(Number(value) || 0);

const toInputDate = (date) => {
  const nextDate = new Date(date);
  return Number.isNaN(nextDate.getTime()) ? '' : nextDate.toISOString().split('T')[0];
};

const buildDefaultFilters = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 29);

  return {
    startDate: toInputDate(start),
    endDate: toInputDate(end),
    groupBy: 'day',
  };
};

const StatCard = ({ icon: Icon, label, value, accent, background, trend }) => (
  <Card
    elevation={0}
    sx={{
      borderRadius: 3.5,
      border: '1px solid',
      borderColor: 'divider',
      height: '100%',
      background,
    }}
  >
    <CardContent sx={{ p: 3.25 }}>
      <Stack spacing={2.5}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box
            sx={{
              width: 50,
              height: 50,
              borderRadius: 2,
              bgcolor: accent.bg,
              color: accent.fg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon size={22} />
          </Box>
          {trend ? (
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: '#16a34a' }}>
              <ArrowUpRight size={14} />
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {trend}
              </Typography>
            </Stack>
          ) : null}
        </Stack>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', lineHeight: 1.1 }}>
            {value}
          </Typography>
          <Typography variant="body1" sx={{ color: '#64748b', mt: 1.25 }}>
            {label}
          </Typography>
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

const ChartCard = ({ title, subtitle, children }) => (
  <Card
    elevation={0}
    sx={{
      height: '100%',
      borderRadius: 4,
      border: '1px solid',
      borderColor: 'divider',
      boxShadow: '0 16px 40px rgba(15, 23, 42, 0.06)',
    }}
  >
    <CardContent sx={{ p: 3 }}>
      <Stack spacing={0.5} mb={2.5}>
        <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a' }}>
          {title}
        </Typography>
        <Typography variant="body2" sx={{ color: '#64748b' }}>
          {subtitle}
        </Typography>
      </Stack>
      {children}
    </CardContent>
  </Card>
);

const EmptyState = ({ label }) => (
  <Stack
    alignItems="center"
    justifyContent="center"
    spacing={1.5}
    sx={{
      minHeight: 300,
      borderRadius: 3,
      border: '1px dashed',
      borderColor: 'divider',
      color: '#64748b',
      textAlign: 'center',
      px: 3,
    }}
  >
    <BarChart3 size={34} />
    <Typography variant="body2">{label}</Typography>
  </Stack>
);

const DashboardPage = ({ session = {} }) => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState(buildDefaultFilters);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dashboard, setDashboard] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const loadDashboard = async () => {
      setLoading(true);
      setError('');

      try {
        const response = await reportsApi.getMessageDashboard(filters);
        if (!cancelled) {
          setDashboard(response?.data?.data || null);
        }
      } catch (err) {
        if (!cancelled) {
          setDashboard(null);
          setError(err?.response?.data?.message || 'Failed to load dashboard analytics.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadDashboard();

    return () => {
      cancelled = true;
    };
  }, [filters]);

  const summary = dashboard?.summary || {};
  const activeCustomers = dashboard?.customerReport?.length || 0;
  const trendRows = dashboard?.trends || [];
  const topCustomers = (dashboard?.customerReport || []).slice(0, 6).map((row) => ({
    name: row.customerName || row.phoneNumber || 'Unknown',
    sent: row.sent || 0,
    received: row.received || 0,
  }));

  const summaryCards = useMemo(
    () => [
      {
        label: 'Total Customers',
        value: formatNumber(activeCustomers),
        icon: Users,
        accent: { bg: '#dcfce7', fg: '#16a34a' },
        background: '#ffffff',
        trend: '+12%',
      },
      {
        label: 'Service Messages',
        value: formatNumber(summary.totalSent),
        icon: MessageSquare,
        accent: { bg: '#dcfce7', fg: '#22c55e' },
        background: '#ffffff',
        trend: '+8%',
      },
      {
        label: 'Business Messages',
        value: formatNumber(summary.totalDelivered),
        icon: BriefcaseBusiness,
        accent: { bg: '#dbeafe', fg: '#2563eb' },
        background: '#ffffff',
        trend: '+5%',
      },
      {
        label: 'Promotions This Month',
        value: formatNumber(summary.totalFailed),
        icon: TriangleAlert,
        accent: { bg: '#fef3c7', fg: '#d97706' },
        background: '#ffffff',
        trend: '+2%',
      },
    ],
    [activeCustomers, summary.totalDelivered, summary.totalFailed, summary.totalSent]
  );

  const handleFilterChange = (field) => (event) => {
    setFilters((current) => ({
      ...current,
      [field]: event.target.value,
    }));
  };

  return (
    <Box sx={{ minHeight: 'calc(100vh - 64px)', bgcolor: '#f8fafc', p: { xs: 2, md: 4 } }}>
      <Box sx={{ maxWidth: 1500, mx: 'auto' }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 800,
            color: '#0f172a',
            fontSize: { xs: '1.55rem', md: '1.95rem' },
            mb: 1,
          }}
        >
          Good evening, {session?.u_name || session?.name || 'Hepto'}
        </Typography>
        <Typography sx={{ color: '#64748b', mb: 4 }}>
          Here&apos;s what&apos;s happening with your business today
        </Typography>

        <Grid container spacing={3} sx={{ mb: 4.5 }}>
          {summaryCards.map((card) => (
            <Grid item xs={12} sm={6} md={3} key={card.label}>
              <StatCard {...card} />
            </Grid>
          ))}
        </Grid>

        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', lg: 'center' }}
          justifyContent="space-between"
          sx={{ mb: 4 }}
        >
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              label="Start Date"
              type="date"
              value={filters.startDate}
              onChange={handleFilterChange('startDate')}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 180, '& .MuiOutlinedInput-root': { bgcolor: '#fff', borderRadius: 2.5 } }}
            />
            <TextField
              label="End Date"
              type="date"
              value={filters.endDate}
              onChange={handleFilterChange('endDate')}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 180, '& .MuiOutlinedInput-root': { bgcolor: '#fff', borderRadius: 2.5 } }}
            />
            <FormControl sx={{ minWidth: 180 }}>
              <InputLabel id="dashboard-group-by-label">Trend View</InputLabel>
              <Select
                labelId="dashboard-group-by-label"
                label="Trend View"
                value={filters.groupBy}
                onChange={handleFilterChange('groupBy')}
                sx={{ bgcolor: '#fff', borderRadius: 2.5 }}
              >
                <MenuItem value="day">Daily</MenuItem>
                <MenuItem value="month">Monthly</MenuItem>
                <MenuItem value="year">Yearly</MenuItem>
              </Select>
            </FormControl>
          </Stack>
          <Stack direction="row" spacing={1.5}>
            <Button
              variant="contained"
              onClick={() => navigate('/reports')}
              sx={{
                bgcolor: '#16a34a',
                borderRadius: 2.5,
                px: 3,
                boxShadow: 'none',
                '&:hover': { bgcolor: '#15803d', boxShadow: 'none' },
              }}
            >
              Explore Insights
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/chat')}
              sx={{
                color: '#0f172a',
                borderColor: '#cbd5e1',
                borderRadius: 2.5,
                px: 3,
              }}
            >
              View Live Chat
            </Button>
          </Stack>
        </Stack>

        {error ? (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        ) : null}

        {loading ? (
          <Stack alignItems="center" justifyContent="center" sx={{ py: 10 }}>
            <CircularProgress />
          </Stack>
        ) : (
          <Stack spacing={3}>
            <ChartCard
              title="Message Trend"
              subtitle="Sent, received, delivered, and failed message movement for the selected period."
            >
              {trendRows.length === 0 ? (
                <EmptyState label="No message trend data found for the selected range." />
              ) : (
                <Box sx={{ height: 360 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={trendRows} margin={{ left: 8, right: 8, top: 8, bottom: 0 }}>
                      <defs>
                        <linearGradient id="sentArea" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#22c55e" stopOpacity={0.28} />
                          <stop offset="95%" stopColor="#22c55e" stopOpacity={0.03} />
                        </linearGradient>
                        <linearGradient id="receivedArea" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#2563eb" stopOpacity={0.22} />
                          <stop offset="95%" stopColor="#2563eb" stopOpacity={0.02} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                      <XAxis dataKey="label" tickLine={false} axisLine={false} />
                      <YAxis tickLine={false} axisLine={false} allowDecimals={false} />
                      <Tooltip />
                      <Area type="monotone" dataKey="sent" stroke="#22c55e" fill="url(#sentArea)" strokeWidth={2.5} />
                      <Area type="monotone" dataKey="received" stroke="#2563eb" fill="url(#receivedArea)" strokeWidth={2.5} />
                      <Area type="monotone" dataKey="failed" stroke="#ef4444" fill="transparent" strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                </Box>
              )}
            </ChartCard>

            <ChartCard
              title="Top Customers by Activity"
              subtitle="Customers with the highest sent and received message volume."
            >
              {topCustomers.length === 0 ? (
                <EmptyState label="No customer activity found for the selected range." />
              ) : (
                <Box sx={{ height: 340 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={topCustomers} layout="vertical" margin={{ left: 8, right: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" />
                      <XAxis type="number" tickLine={false} axisLine={false} allowDecimals={false} />
                      <YAxis
                        type="category"
                        dataKey="name"
                        tickLine={false}
                        axisLine={false}
                        width={140}
                      />
                      <Tooltip />
                      <Bar dataKey="sent" fill="#22c55e" radius={[0, 8, 8, 0]} />
                      <Bar dataKey="received" fill="#2563eb" radius={[0, 8, 8, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              )}
            </ChartCard>
          </Stack>
        )}
      </Box>
    </Box>
  );
};

export default DashboardPage;
